"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Chess, type Square } from "chess.js";
import { BoardView } from "@/components/board/BoardView";
import type { MoveIntent } from "@/components/board/interaction";
import { playMoveSound } from "@/lib/sound";

type TbMove = { uci: string; san: string; category: string; dtm?: number | null; dtz?: number | null };
type Tb = { category: string; dtm?: number | null; dtz?: number | null; moves: TbMove[] };

const CATEGORY: Record<string, string> = {
  win: "winning",
  loss: "losing",
  draw: "drawn",
  "cursed-win": "a win that the fifty-move rule turns into a draw",
  "blessed-loss": "a loss that the fifty-move rule saves",
  "maybe-win": "probably winning",
  "maybe-loss": "probably losing",
  unknown: "unknown",
};

async function probe(fen: string): Promise<Tb | null> {
  try {
    const r = await fetch(`/api/tablebase?fen=${encodeURIComponent(fen)}`);
    return r.ok ? ((await r.json()) as Tb) : null;
  } catch {
    return null;
  }
}

/**
 * Play an endgame against perfect defence. The tablebase picks the defender's
 * moves and grades every move the reader makes.
 */
export function EndgameTrainer({ fen: startFen, side, goal, ideas = [] }: { fen: string; side: "white" | "black"; goal: "win" | "draw"; ideas?: string[] }) {
  const [focus, setFocus] = useState(0);
  const requestId = useRef({ version: 0 });
  const [hint, setHint] = useState(false);
  const game = useRef(new Chess(startFen));
  const [fen, setFen] = useState(startFen);
  const [last, setLast] = useState<{ from: Square; to: Square } | null>(null);
  const [msg, setMsg] = useState<{ tone: "info" | "good" | "bad"; text: string }>({
    tone: "info",
    text: goal === "win" ? "Your move. Win against perfect defence." : "Your move. Hold the draw against perfect play.",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [moves, setMoves] = useState(0);
  const [status, setStatus] = useState<Tb | null>(null);
  const myColor = side === "white" ? "w" : "b";

  useEffect(() => {
    let active = true;
    const requests = requestId.current;
    const revision = requests.version;
    void probe(startFen).then((result) => { if (active && requests.version === revision) setStatus(result); });
    return () => { active = false; requests.version++; };
  }, [startFen]);

  const reset = useCallback(() => {
    const revision = ++requestId.current.version;
    setBusy(false);
    setHint(false);
    setStatus(null);
    game.current = new Chess(startFen);
    setFen(startFen);
    setLast(null);
    setDone(false);
    setMoves(0);
    setMsg({ tone: "info", text: "Position reset. Your move." });
    void probe(startFen).then((result) => { if (revision === requestId.current.version) setStatus(result); });
  }, [startFen]);

  const finishIfOver = (c: Chess): boolean => {
    if (c.isCheckmate()) {
      const iWon = c.turn() !== myColor;
      setMsg(iWon ? { tone: "good", text: "Checkmate. Well played." } : { tone: "bad", text: "Checkmated. Reset and try again." });
      setDone(true);
      playMoveSound("end");
      return true;
    }
    if (c.isDraw()) {
      const ok = goal === "draw";
      setMsg(ok ? { tone: "good", text: "Drawn. You held it." } : { tone: "bad", text: "The game is drawn. Reset and look for a faster win." });
      setDone(true);
      return true;
    }
    return false;
  };

  const onMove = (m: MoveIntent): boolean => {
    if (busy || done) return false;
    const revision = ++requestId.current.version;
    setHint(false);
    const c = game.current;
    let mv;
    try {
      mv = c.move({ from: m.from, to: m.to, promotion: m.promotion });
    } catch {
      return false;
    }
    setFen(c.fen());
    setLast({ from: mv.from, to: mv.to });
    playMoveSound(mv.captured ? "capture" : "move");
    const n = moves + 1;
    setMoves(n);
    if (finishIfOver(c)) return true;

    setBusy(true);
    void (async () => {
      const tb = await probe(c.fen());
      if (revision !== requestId.current.version) return;
      if (!tb || !tb.moves.length) {
        c.undo(); setFen(c.fen()); setLast(last); setMoves(n - 1);
        setMsg({ tone: "bad", text: "The tablebase is unavailable right now. Your move was restored; try again shortly or revisit the demonstration above." });
        setBusy(false);
        return;
      }
      // tb.category is from the defender's point of view now; status is from the reader's,
      // before this move. Only report the move that changed the result, not every move after it.
      const wasWinning = !status || status.category === "win" || status.category === "maybe-win";
      const wasHolding = status?.category !== "loss" && status?.category !== "maybe-loss";
      const lostTheWin = goal === "win" && wasWinning && tb.category !== "loss" && tb.category !== "maybe-loss";
      const lostTheDraw = goal === "draw" && wasHolding && (tb.category === "win" || tb.category === "maybe-win");
      const reply = tb.moves[0];
      await new Promise((r) => setTimeout(r, 450));
      if (revision !== requestId.current.version) return;
      const r = c.move({ from: reply.uci.slice(0, 2), to: reply.uci.slice(2, 4), promotion: reply.uci[4] });
      setFen(c.fen());
      setLast({ from: r.from, to: r.to });
      playMoveSound(r.captured ? "capture" : "move");
      if (!finishIfOver(c)) {
        const after = await probe(c.fen());
        if (revision !== requestId.current.version) return;
        setStatus(after);
        if (lostTheWin) setMsg({ tone: "bad", text: `${mv.san} let the win slip: the position is now ${CATEGORY[tb.category] ?? tb.category}. Reset to try again, or play on.` });
        else if (lostTheDraw) setMsg({ tone: "bad", text: `${mv.san} loses by force. Reset to try again.` });
        else if (goal === "draw" && n >= 15) {
          setMsg({ tone: "good", text: "Fifteen moves of perfect defence. You know how to hold this." });
          setDone(true);
        } else setMsg({ tone: "info", text: `${mv.san}, reply ${r.san}. ${after?.dtm ? `Mate in ${Math.ceil(Math.abs(after.dtm) / 2)} with best play.` : ""}` });
      }
      setBusy(false);
    })();
    return true;
  };

  const tone = msg.tone === "good" ? "border-accent bg-accent-soft" : msg.tone === "bad" ? "border-warn bg-warn/10" : "border-border bg-surface";
  return (
    <div className="lesson-exercise-collection lesson-demo-layout">
      <BoardView cameraPreset="club" view="3d" fen={fen} orientation={side} movable={busy || done ? "none" : side} lastMove={last} arrows={hint && !busy && status?.moves[0] ? [{ from: status.moves[0].uci.slice(0, 2) as Square, to: status.moves[0].uci.slice(2, 4) as Square, color: "#a9533bcc" }] : []} onMove={onMove} label="Endgame trainer" />
      <div className="lesson-demo-coach space-y-3"><p className="lesson-eyebrow">Your turn · {side}</p><h3>{goal === "win" ? "Build the win." : "Find the defence."}</h3>
        <p className="lesson-demo-note">{goal === "win" ? "Work toward checkmate or promotion. Every move should improve your position, not just give check." : "Keep your position defensible. Patient, accurate moves matter more than winning material."}</p>
        {ideas.length > 0 && <div className="endgame-plan"><p className="lesson-eyebrow">Your technique checklist</p><p className="lesson-demo-note">Choose an idea to focus on as you play.</p>{ideas.map((idea, index) => <button key={idea} aria-pressed={focus === index} onClick={() => setFocus(index)}><span>{String(index + 1).padStart(2, "0")}</span>{idea}</button>)}</div>}
        <div className="practice-sequence"><span>{moves} {moves === 1 ? "move" : "moves"} practised</span><span>{done ? "Attempt finished" : busy ? "Defender is thinking…" : "Select a piece, then a square"}</span></div>
        <div className={`rounded-xl border p-3 text-sm ${tone}`} role="status" aria-live="polite">
          {msg.text}
        </div>
        {status && (
          <p className="text-sm text-muted">
            Tablebase: the side to move is {CATEGORY[status.category] ?? status.category}
            {status.dtm ? `, mate in ${Math.ceil(Math.abs(status.dtm) / 2)}` : ""}.
          </p>
        )}
        {status?.moves.length && !done ? <button className="button-secondary" disabled={busy} aria-pressed={hint} onClick={() => setHint(!hint)}>{hint ? "Hide hint" : "Show a tablebase hint"}</button> : null}
        {hint && status?.moves[0] && <p role="status" className="lesson-demo-note">Consider {status.moves[0].san}. Follow the arrow, then think about how this move serves your plan.</p>}
        <button onClick={reset} className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm hover:bg-surface-2">
          Reset position
        </button>
      </div>
    </div>
  );
}

